// src/widgets/UserByTenantWidget.jsx
//
// Widget: ranking tenant berdasarkan jumlah radius user.
// Mendukung filter per radius_server_id.
//
// Props:
//   filter  {object}  - { radius_server_id, limit }

import useWidgetData from '../hooks/useWidgetData';
import { Building2 } from 'lucide-react';

const STATUS_COLORS = {
  active:     'var(--accent-green, #22c55e)',
  inactive:   'var(--text-muted)',
  suspended:  'var(--accent-orange)',
  expired:    'var(--accent-red)',
  pre_active: 'var(--accent-blue)',
};

export default function UserByTenantWidget({ filter = {} }) {
  const { data, loading, error } = useWidgetData('radius_user_by_tenant', filter);
  const items = data?.items || [];
  const maxTotal = items.length > 0 ? items[0].total : 1;

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '16px 18px', boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          User per Tenant
        </div>
        <div style={{ padding: 6, background: 'rgba(99,102,241,0.12)', borderRadius: 6 }}>
          <Building2 size={14} color="var(--accent-purple, #818cf8)" />
        </div>
      </div>

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Memuat…</div>
      ) : error ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-red)', fontSize: 12 }}>{error}</div>
      ) : items.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Tidak ada data</div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((item, i) => {
            const pct = maxTotal > 0 ? (item.total / maxTotal) * 100 : 0;
            const activeCount = item.by_status?.active || 0;
            const activePct   = item.total > 0 ? ((activeCount / item.total) * 100).toFixed(0) : 0;

            return (
              <div key={item.tenant_id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{
                      width: 18, height: 18, borderRadius: 4, background: i < 3 ? 'var(--accent-purple, #818cf8)' : 'var(--bg-input)',
                      color: i < 3 ? '#fff' : 'var(--text-muted)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 700, flexShrink: 0,
                    }}>{i + 1}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={item.tenant_name}>
                      {item.tenant_name || `Tenant #${item.tenant_id}`}
                    </span>
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', flexShrink: 0, marginLeft: 8 }}>
                    {item.total.toLocaleString()}
                  </span>
                </div>

                {/* Stacked bar: by status */}
                <div style={{ display: 'flex', height: 4, borderRadius: 2, overflow: 'hidden', gap: 1, marginLeft: 26 }}>
                  {Object.entries(item.by_status || {}).map(([status, count]) => (
                    <div key={status}
                      title={`${status}: ${count}`}
                      style={{
                        flex: count,
                        background: STATUS_COLORS[status] || 'var(--border)',
                        minWidth: 2,
                      }}
                    />
                  ))}
                </div>

                {/* Sub info */}
                <div style={{ display: 'flex', gap: 8, paddingLeft: 26 }}>
                  <span style={{ fontSize: 10, color: 'var(--accent-green, #22c55e)' }}>
                    {activeCount} active ({activePct}%)
                  </span>
                  {Object.entries(item.by_status || {})
                    .filter(([s]) => s !== 'active' && item.by_status[s] > 0)
                    .map(([s, c]) => (
                      <span key={s} style={{ fontSize: 10, color: STATUS_COLORS[s] || 'var(--text-muted)' }}>
                        {c} {s}
                      </span>
                    ))
                  }
                </div>

                {/* Width bar */}
                <div style={{ height: 2, background: 'var(--bg-input)', borderRadius: 1, marginLeft: 26 }}>
                  <div style={{ width: `${pct}%`, minWidth: 2, height: '100%', background: 'var(--accent-purple, #818cf8)', borderRadius: 1 }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}